/**
 * DriveModeToggle — calm consent-based moving_state selector.
 *
 * Stage-2 P1 follow-up — Drive Mode P1 (Feb 18, 2026).
 *
 * Operator brief:
 *   "Add a small Drive Mode state control for Work Mode:
 *    'I'm parked' / 'I'm driving'. Consent-based.
 *    NO auto-detection, NO GPS, NO sensors."
 *
 * Doctrine:
 *   • One-tap selection. Either button is a 1-tap call to
 *     `setMovingState`. No menus, no confirmations, no popups.
 *   • Single source of truth — delegates to `useDriveModeState`
 *     which persists to localStorage and broadcasts cross-tab.
 *   • Calm slate palette. No emerald/red "alert" colours.
 *     Selected state is a subtle filled chip; the other is an
 *     outline chip. No animation beyond the implicit hover
 *     hairline.
 *   • Returns the same toggle UI under both surfaces — light
 *     (default) and dark (for the Work Mode Live view).
 */
import { Square, Car } from "lucide-react";
import useDriveModeState from "../../hooks/useDriveModeState";


export default function DriveModeToggle({ surface = "light" }) {
  const { movingState, setMovingState } = useDriveModeState();
  const isDark = surface === "dark";

  const baseCls = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors";
  const offCls = isDark
    ? "border-slate-700 text-slate-300 hover:border-slate-500"
    : "border-slate-200 text-slate-600 hover:border-slate-300";
  const onParked = movingState === "parked";
  const onMoving = movingState === "moving";
  const parkedCls = onParked
    ? (isDark
      ? "bg-slate-200 text-slate-900 border-slate-200"
      : "bg-slate-900 text-white border-slate-900")
    : offCls;
  const movingCls = onMoving
    ? (isDark
      ? "bg-amber-200 text-amber-950 border-amber-200"
      : "bg-amber-100 text-amber-900 border-amber-300")
    : offCls;

  return (
    <div
      data-testid="drive-mode-toggle"
      data-moving-state={movingState}
      className="inline-flex items-center gap-2"
      role="group"
      aria-label="Drive Mode state"
    >
      <button
        type="button"
        data-testid="drive-mode-toggle-parked"
        aria-pressed={onParked}
        onClick={() => setMovingState("parked")}
        className={`${baseCls} ${parkedCls}`}
      >
        <Square className="w-3.5 h-3.5" />
        I&apos;m parked
      </button>
      <button
        type="button"
        data-testid="drive-mode-toggle-moving"
        aria-pressed={onMoving}
        onClick={() => setMovingState("moving")}
        className={`${baseCls} ${movingCls}`}
      >
        <Car className="w-3.5 h-3.5" />
        I&apos;m driving
      </button>
    </div>
  );
}
